import {
  DeleteIcon,
  EditIcon,
  Group,
  InfiniteTable,
} from '@/components/_common';
import StatusTag from '@/components/_common/StatusTag';
import {
  Purchase,
  PurchaseItemType,
} from '@/core/domain/purchase/Purchase.types';
import {
  useItemPurchaseRemove,
  useItemsPurchases,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import {
  PurchaseItem,
  purchaseItemTypeColor,
  purchaseItemTypeText,
} from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { quantityFormatter } from '@/utils/number';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { getTableScrollX } from '@/utils/table';
import { Divider, Modal, Popover, Space, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { FaInfoCircle, FaQuestionCircle } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import PurchaseItemSellPrice from './PurchaseItemSellPrice';
import { Status } from '@/utils/enum/statusEnum';
import { useCompanySettings } from '@/core/domain/enterprise/Companies/Companies.hooks';

interface Props {
  purchaseId: number;
  purchaseItems?: PurchaseItem[];
  purchase?: Purchase;
  isCanceled?: boolean;
}

const PurchaseItemsPurchaseList: React.FC<Props> = ({
  purchaseId,
  isCanceled,
  purchaseItems,
  purchase,
}) => {
  const intl = useIntl();
  const history = useHistory();

  const { data, isFetching, hasNextPage, fetchNextPage } = useItemsPurchases({
    order: 'DESC',
    purchaseId,
    enabled: !purchaseItems?.length,
  });
  const configCompany = useCompanySettings(purchase?.companyId);

  const itemsPurchase = flatInfiniteData(data);

  const removeItemPurchaseMutation = useItemPurchaseRemove();

  const handleRemoveClick = (itemPurchaseId: number) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'purchases.details.itemsPurchase.notifications.remove.confirm.title',
      }),
      content: `${intl.formatMessage({
        id: 'purchases.details.itemsPurchase.notifications.remove.confirm.content',
      })}`,
      onOk: async () => {
        await removeItemPurchaseMutation.mutateAsync({
          id: itemPurchaseId,
          purchaseId,
        });
      },
      okType: 'danger',
    });
  };
  const sellPriceInformedColumn: ColumnsType<PurchaseItem> =
    purchase?.situation === Status.open &&
    !!configCompany.data?.allowInformSellPriceInPurchaseItem
      ? [
          {
            key: 'sellPriceInformed',
            dataIndex: 'sellPriceInformed',
            title: (
              <Group gap={5}>
                Pr. Venda{' '}
                <Popover
                  title="Como funciona a atualização do preço de venda"
                  content={
                    <div style={{ maxWidth: 400 }}>
                      <p>
                        Ao autorizar a compra, se a opção{' '}
                        <strong>"Alterar preço de venda"</strong> estiver
                        marcada, o sistema recalculará o{' '}
                        <strong>percentual de lucro</strong> com base no preço
                        de venda informado e no preço de custo real.
                      </p>
                      <p>
                        Essa atualização será feita apenas para os itens que
                        possuírem um preço de venda informado.
                      </p>
                    </div>
                  }
                >
                  <FaQuestionCircle size={12} />
                </Popover>
              </Group>
            ),
            width: 150,
            render: (_, item) => <PurchaseItemSellPrice item={item} />,
          },
        ]
      : [];

  const columns: ColumnsType<PurchaseItem> = [
    {
      key: 'workOrderNumber',
      dataIndex: 'workOrderNumber',
      title: intl.formatMessage({
        id: 'purchases.details.itemsPurchase.table.budget',
      }),
      ellipsis: true,
      width: 120,
      render: (value) => (value ? value : '-'),
    },
    useConfigurationColumnsPart({ isSku: true, barcode: 'barCode' }),
    {
      key: 'type',
      dataIndex: 'type',
      title: 'Tipo',
      width: 150,
      render: (type: PurchaseItemType) => (
        <StatusTag
          color={purchaseItemTypeColor[type]}
          label={purchaseItemTypeText[type]}
        />
      ),
    },
    {
      key: 'name',
      dataIndex: 'name',
      title: intl.formatMessage({
        id: `purchases.details.itemsPurchase.table.description`,
      }),
      ellipsis: true,
      width: 250,
    },
    {
      key: 'manufacturingName',
      dataIndex: 'manufacturingName',
      title: intl.formatMessage({
        id: `purchases.details.itemsPurchase.table.manufacturer`,
      }),
      ellipsis: true,
      width: 200,
    },
    {
      key: 'unitMeasurement',
      dataIndex: 'unitMeasurement',
      title: 'Unidade',
      ellipsis: true,
      width: 90,
    },
    {
      key: 'quantity',
      dataIndex: 'quantity',
      title: intl.formatMessage({
        id: `purchases.details.itemsPurchase.table.qty`,
      }),
      ellipsis: true,
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
    {
      key: 'unitaryValue',
      dataIndex: 'unitaryValue',
      title: intl.formatMessage({
        id: `purchases.details.itemsPurchase.table.unitPrice`,
      }),
      ellipsis: true,
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
    {
      key: 'discount',
      dataIndex: 'discount',
      title: intl.formatMessage({
        id: `purchases.details.itemsPurchase.table.discount`,
      }),
      ellipsis: true,
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
    {
      key: 'totalAmount',
      dataIndex: 'totalAmount',
      title: intl.formatMessage({
        id: `purchases.details.itemsPurchase.table.amount`,
      }),
      ellipsis: true,
      width: 100,
      render: (value: number) => quantityFormatter(value),
    },
    ...sellPriceInformedColumn,
    !purchaseItems?.length
      ? {
          key: 'actions',
          title: intl.formatMessage({ id: 'purchases.list.header.actions' }),
          render: (_, record) => (
            <Space>
              {record?.amountReturned > 0 && (
                <>
                  <Tooltip
                    title={`Foram devolvidas ${quantityFormatter(
                      record.amountReturned,
                    )} unidades desse item!`}
                  >
                    <FaInfoCircle size={20} color="orange" />
                  </Tooltip>

                  <Divider type="vertical" />
                </>
              )}

              <EditIcon
                iconProps={{
                  onClick: () =>
                    history.push(
                      `itemsPurchase/detailItemPurchase/item/${record.id}/detail`,
                    ),
                }}
                disabled={
                  isCanceled ||
                  !hasPermissions([
                    Authorities.MOVIMENTATION_PURCHASE_ITEM_EDIT,
                  ])
                }
              />

              <Divider type="vertical" />

              <DeleteIcon
                onClick={() => handleRemoveClick(record.id)}
                disabled={
                  isCanceled ||
                  purchase?.isGrouping ||
                  purchase?.boschMigration ||
                  !hasPermissions([
                    Authorities.MOVIMENTATION_PURCHASE_ITEM_DELETE,
                  ])
                }
              />
            </Space>
          ),
          width: 135,
          fixed: 'right',
          align: 'right',
        }
      : {},
  ];

  return (
    <InfiniteTable<PurchaseItem>
      columns={columns}
      dataSource={purchaseItems?.length ? purchaseItems : itemsPurchase}
      loading={isFetching}
      rowKey={(row) => row.id}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      scroll={{ x: getTableScrollX(columns) }}
    />
  );
};

export default PurchaseItemsPurchaseList;
